import { flatten } from 'es-toolkit/compat';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { useAppDispatch } from '../../../../configuration/setup/store';
import { fetchCheckoutDiscountDataThunk } from '../../common/discount/discounts.thunk';
import { parseQueryString } from '../../common/utils/qsUtil';
import { checkoutActions, getSelectedProductId, getSelectedProductLevel } from '../redux/checkout.redux';
import { resourceSelectionActions } from '../resourceSelection/redux/resourceSelection.redux';
import { fetchBookableResourcesThunk } from '../resourceSelection/resourceSelection.thunk';
import { fetchCheckoutProductDetailsThunk } from '../thunks/productDetails.thunk';
import { UrlParameterKeys } from './UrlParameterKeys';

interface CheckoutRouteState {
    resourceIds?: string[];
    productId?: string;
    level?: string;
    locked?: boolean;
    internalCampaign?: string;
}

const readArray = (selectedIds: unknown): string[] | undefined => {
    if (typeof selectedIds === 'string') {
        return selectedIds ? [selectedIds] : undefined;
    }
    if (Array.isArray(selectedIds)) {
        const ids = flatten(selectedIds).filter((id): id is string => typeof id === 'string');
        return ids.length > 0 ? ids : undefined;
    }
    if (selectedIds && typeof selectedIds === 'object') {
        // qs turns comma-separated arrays with more than 20 entries into numeric-keyed objects.
        const entries = Object.entries(selectedIds);
        if (entries.length > 0 && entries.every(([index, id]) => /^\d+$/.test(index) && typeof id === 'string')) {
            return entries.sort(([left], [right]) => Number(left) - Number(right)).map(([, id]) => id as string);
        }
    }
    return undefined;
};

const parseCheckoutRoute = (search: string): CheckoutRouteState => {
    const parsedParams = parseQueryString(search);

    const resourceIds = parsedParams[UrlParameterKeys.RESOURCE_IDS] ?? parsedParams[UrlParameterKeys.ASSET_IDS];
    const productId = parsedParams[UrlParameterKeys.PRODUCT_ID];
    const level = parsedParams[UrlParameterKeys.LEVEL];
    const locked = parsedParams[UrlParameterKeys.LOCKED];
    const internalCampaign = parsedParams[UrlParameterKeys.INTERNAL_CAMPAIGN];

    return {
        resourceIds: readArray(resourceIds),
        productId: productId as string,
        level: level as string,
        locked: locked === undefined ? undefined : locked === 'true',
        internalCampaign: internalCampaign as string,
    };
};

export function useCheckoutDeepLinking() {
    const { search } = useLocation();
    const dispatch = useAppDispatch();
    const selectedProductId = useSelector(getSelectedProductId);
    const productLevel = useSelector(getSelectedProductLevel);

    useEffect(() => {
        const { resourceIds, productId, level, locked, internalCampaign } = parseCheckoutRoute(search);

        resourceIds?.forEach(assetId => {
            dispatch(resourceSelectionActions.selectResource(assetId));
        });
        if (productId) {
            dispatch(checkoutActions.setSelectedProductId(productId));
        }
        if (level) {
            dispatch(checkoutActions.setSelectedProductLevel(level));
        }
        if (internalCampaign) {
            dispatch(checkoutActions.setInternalCampaign(internalCampaign));
        }
        const resourcesGiven = locked === undefined && resourceIds && resourceIds.length > 0;
        dispatch(checkoutActions.setCheckoutIsLocked(resourcesGiven || !!locked));
    }, []);

    useEffect(() => {
        if (selectedProductId) {
            dispatch(fetchCheckoutProductDetailsThunk);
        }
    }, [selectedProductId, fetchCheckoutProductDetailsThunk]);

    useEffect(() => {
        if (selectedProductId) {
            dispatch(fetchBookableResourcesThunk);
            dispatch(fetchCheckoutDiscountDataThunk);
        }
    }, [selectedProductId, productLevel]);
}
